import { apiRequest } from '../../lib/api-client.js';

export interface Server {
  id: number;
  name: string;
  url: string;
  key_label: string;
  status: 'active' | 'inactive';
  last_health_check_at: string | null;
  last_health_snapshot: HealthSnapshot | null;
  created_at: string;
}

export interface HealthSnapshot {
  ok: boolean;
  api_version: string;
  timestamp: string;
  counters: {
    tenants: { total: number; by_status: Record<string, number> };
    users_active: number;
    messages_last_30d: number;
  };
}

export interface Tenant {
  id: number;
  name: string;
  slug: string;
  status: 'active' | 'suspended' | 'trial';
  retention_days: number;
  /** Chat interno habilitado pro tenant (flag de plano — default false). */
  internal_chat_enabled: boolean;
  voip_enabled?: boolean;
  ai_rewrite_enabled?: boolean;
  ai_rewrite_monthly_limit?: number;
  /** Limite de canais do plano. null = sem limite. */
  max_channels: number | null;
  /** Limite de usuários ativos do plano. null = sem limite. */
  max_users: number | null;
  created_at: string;
  users: { total: number; active: number };
  /** Canais VIVOS (não-deletados, não-revogados) — mesmo critério da trava. */
  channels_active: number;
}

export interface TenantDetail extends Tenant {
  updated_at: string;
  counters: {
    users: { total: number; active: number };
    messages_last_30d: number;
    channels: number;
  };
}

export interface CreateServerInput {
  name: string;
  url: string;
  key: string;
  key_label: string;
}

export interface PatchServerInput {
  name?: string;
  url?: string;
  key?: string;
  key_label?: string;
  status?: 'active' | 'inactive';
}

export interface CreateTenantInput {
  name: string;
  slug: string;
  retention_days?: number;
  admin: { email: string; password: string; name: string };
}

export interface PatchTenantInput {
  name?: string;
  status?: 'active' | 'suspended';
  retention_days?: number;
  internal_chat_enabled?: boolean;
  voip_enabled?: boolean;
  max_channels?: number | null;
  max_users?: number | null;
  ai_rewrite_enabled?: boolean;
  ai_rewrite_monthly_limit?: number;
}

export interface TenantUser {
  id: number;
  email: string;
  name: string;
  profile: 'admin' | 'attendant';
  status: 'active' | 'inactive';
  last_login_at: string | null;
  created_at: string;
}

export const serversApi = {
  list: () => apiRequest<Server[]>('/api/servers'),
  create: (body: CreateServerInput) =>
    apiRequest<Server>('/api/servers', { method: 'POST', body }),
  patch: (id: number, body: PatchServerInput) =>
    apiRequest<Server>(`/api/servers/${id}`, { method: 'PATCH', body }),
  delete: (id: number) => apiRequest<void>(`/api/servers/${id}`, { method: 'DELETE' }),
  health: (id: number) =>
    apiRequest<{ status: number } & HealthSnapshot>(`/api/servers/${id}/health`),
  tenants: (id: number) => apiRequest<Tenant[]>(`/api/servers/${id}/tenants`),
  tenantDetail: (id: number, tid: number) =>
    apiRequest<TenantDetail>(`/api/servers/${id}/tenants/${tid}`),
  createTenant: (id: number, body: CreateTenantInput) =>
    apiRequest<{ id: number; admin: { id: number; email: string } } & Tenant>(
      `/api/servers/${id}/tenants`,
      { method: 'POST', body },
    ),
  patchTenant: (id: number, tid: number, body: PatchTenantInput) =>
    apiRequest<Tenant & { revoked_user_count?: number }>(
      `/api/servers/${id}/tenants/${tid}`,
      { method: 'PATCH', body },
    ),
  tenantUsers: (id: number, tid: number) =>
    apiRequest<TenantUser[]>(`/api/servers/${id}/tenants/${tid}/users`),
  resetTenantUserPassword: (id: number, tid: number, uid: number, password: string) =>
    apiRequest<void>(
      `/api/servers/${id}/tenants/${tid}/users/${uid}/reset-password`,
      { method: 'POST', body: { password } },
    ),
  patchTenantUser: (id: number, tid: number, uid: number, status: 'active' | 'inactive') =>
    apiRequest<{ id: number; status: 'active' | 'inactive' }>(
      `/api/servers/${id}/tenants/${tid}/users/${uid}`,
      { method: 'PATCH', body: { status } },
    ),
};
